/**
 * RIDE88 — Force Variant Selection (+ STOQ preorder sync)
 * --------------------------------------------------------------------------
 * Requires customers to consciously select every variant option before the
 * "Add to Cart" button becomes active, on:
 *   - the main product page
 *   - the quick view drawer
 *   - embedded blocks like the Featured Product section
 *
 * IMPORTANT: This file is intentionally SEPARATE from product-info.js so it
 * SURVIVES THEME UPDATES. Theme updates reset vendor files (product-info.js)
 * but do not touch files the vendor never created (this one) or theme.liquid.
 * It is loaded from theme.liquid via a <script> tag — do NOT paste this into
 * product-info.js, or it will be wiped on the next theme update.
 *
 * HOW IT HANDLES DROPDOWNS vs SWATCHES:
 *   - Native <select> dropdowns always have SOMETHING selected, so to create a
 *     true "nothing chosen" state we inject a disabled "Choose ..." placeholder
 *     option and keep it selected. This also keeps a `selected` attribute on the
 *     <select> at all times, which the theme's variant-selects.js REQUIRES — it
 *     calls `.find(option[selected]).removeAttribute(...)` on change and would
 *     throw (breaking Add to Cart) if no option had the attribute.
 *   - Radio/swatch inputs support a genuine "nothing checked" state, so we just
 *     uncheck them.
 *
 * --------------------------------------------------------------------------
 * STOQ PREORDER SYNC
 * --------------------------------------------------------------------------
 * STOQ's app embed paints its preorder UI (button label, discount price,
 * payment options) ONCE, on page load. This theme changes variants over AJAX:
 * product-info.js re-renders the section and then, in handleUpdateProductInfo,
 *   - replaces #price-<section> innerHTML with fresh SERVER HTML, and
 *   - rewrites the [name="add"] button's <span> via updateButtonsState()
 * Both of those overwrite whatever STOQ painted, and STOQ never re-runs. That
 * is why the sale price vanishes mid-selection and the button reverts to
 * "Add to cart" instead of "Preorder".
 *
 * Fix: after each theme re-render, once EVERY option group has been chosen,
 * call STOQ's documented hook for exactly this case:
 *     window._RestockRocket.renderButtonForVariant(variantId)
 * (https://docs.stoqapp.com/frontend/docs/javascript-api/)
 *
 * --------------------------------------------------------------------------
 * FORCED SELECTION vs STOQ SYNC — these are two separate jobs
 * --------------------------------------------------------------------------
 * A previous revision returned out of initForceVariantSelection early whenever
 * the page loaded with ?variant= in the URL (a direct variant link), because
 * forcing the customer to re-pick options they had already chosen via the link
 * makes no sense. But the STOQ sync was wired up BELOW that return, so on those
 * URLs none of it existed: no variant:changed listener, no stoq:loaded
 * listener, no settle timer, no price mask. STOQ painted its preorder UI, the
 * theme re-rendered over the top of it, and nothing repainted it. Symptom: the
 * preorder UI appears on load and then vanishes a moment later, leaving a
 * normal-looking product page that still opens the STOQ modal on Add to Cart.
 *
 * Now the two jobs are decoupled. `forceSelection` turns the option-clearing
 * and button-locking behaviour on or off, while the STOQ sync ALWAYS runs.
 * When forceSelection is false, allOptionsChosen() reports true, so reconcile()
 * always takes its "all chosen" branch and simply hands the button to STOQ.
 *
 * --------------------------------------------------------------------------
 * BUDGET RULE — read before editing
 * --------------------------------------------------------------------------
 * Clearing untouched option groups is NEVER rate-limited. An earlier revision
 * put clearUnselectedGroups() behind a shared re-assert counter; page-load
 * chatter (the settle timer, stoq:loaded, STOQ's own paint) drained that
 * counter before the customer clicked anything, so by the second dropdown the
 * clearing silently stopped and the third field appeared to auto-fill.
 *
 * Only the button-vs-STOQ tug-of-war is budgeted, and that budget RESETS on
 * every customer interaction. Do not reintroduce a cap on the clearing path.
 *
 * DEBUG: set window.R88_DEBUG = true (or add ?r88debug=1 to the URL) for
 * console tracing. window.R88ForceSelect.debugStoq() dumps what the STOQ
 * embed actually exposes on this page.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var PLACEHOLDER_ATTR = 'data-force-placeholder';
  var PLACEHOLDER_TEXT = 'Select an option';
  var INCOMPLETE_CLASS = 'r88-force-select--incomplete';
  var BUTTON_REASSERT_BUDGET = 6;

  // Repaint STOQ's preorder UI even when only some options are chosen, so the
  // discounted price survives the theme's mid-selection re-render. The buy
  // button is re-locked immediately afterwards either way. Set to false to go
  // back to only repainting once every option group has been chosen.
  var SYNC_STOQ_WHILE_INCOMPLETE = true;

  // STOQ's repaint is asynchronous internally, so even calling it in the same
  // synchronous block as the theme's overwrite still leaves a frame or two
  // where the undiscounted server price is on screen. We can't win that race,
  // so we hide the price node the instant the theme writes it and reveal it
  // once STOQ has decorated it (or after MASK_MAX_MS, whichever comes first).
  // Opacity rather than display, so the layout never shifts.
  var MASK_PRICE_DURING_SYNC = true;
  var MASK_MAX_MS = 400; // safety net if STOQ never touches the price
  var MASK_FADE_MS = 120;

  var DEBUG =
    window.R88_DEBUG === true ||
    new URLSearchParams(window.location.search).has('r88debug');

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[R88]');
    console.log.apply(console, args);
  }

  // Snapshot whether the page originally loaded with ?variant=... in the URL,
  // before the theme starts rewriting the URL on option changes.
  var originalUrlHasVariant = new URLSearchParams(window.location.search).has('variant');

  function stoqApi() {
    return window._RestockRocket || null;
  }

  function stoqReady() {
    var api = stoqApi();
    return !!(api && typeof api.renderButtonForVariant === 'function');
  }

  // Exposed for troubleshooting from the browser console.
  window.R88ForceSelect = window.R88ForceSelect || {};
  window.R88ForceSelect.debugStoq = function () {
    var api = stoqApi();
    if (!api) {
      console.warn('[R88] window._RestockRocket is not present. Either the STOQ app embed is off, or it has not loaded yet.');
      return null;
    }
    var methods = [];
    for (var key in api) {
      if (typeof api[key] === 'function') methods.push(key);
    }
    console.log('[R88] _RestockRocket methods:', methods.sort());
    console.log('[R88] renderButtonForVariant present:', typeof api.renderButtonForVariant === 'function');
    console.log('[R88] getSellingPlan present:', typeof api.getSellingPlan === 'function');
    var input =
      document.querySelector('input[name="id"].product-variant-id') ||
      document.querySelector('input[name="id"]');
    var raw = input && input.value;
    console.log('[R88] current variant id in form:', raw);
    if (raw && typeof api.getSellingPlan === 'function') {
      // STOQ's methods default to its internal s.selected_variant_id, which
      // Shopify supplies as a NUMBER. input.value is always a string, so a
      // strict lookup on the string misses. Test both so the difference is
      // visible rather than inferred.
      try {
        console.log('[R88] getSellingPlan(String):', api.getSellingPlan(String(raw)));
      } catch (e) {
        console.warn('[R88] getSellingPlan(String) threw', e);
      }
      try {
        console.log('[R88] getSellingPlan(Number):', api.getSellingPlan(Number(raw)));
      } catch (e) {
        console.warn('[R88] getSellingPlan(Number) threw', e);
      }
    }
    return api;
  };

  // Manual probe: window.R88ForceSelect.tryStoq() repaints STOQ for whatever
  // variant the form currently holds. Use it from the console to test calls
  // without reloading.
  window.R88ForceSelect.tryStoq = function (method) {
    var api = stoqApi();
    if (!api) return console.warn('[R88] no _RestockRocket');
    var input =
      document.querySelector('input[name="id"].product-variant-id') ||
      document.querySelector('input[name="id"]');
    var id = Number(input && input.value);
    var fn = method || 'renderButtonForVariant';
    if (typeof api[fn] !== 'function') return console.warn('[R88] no such method:', fn);
    console.log('[R88] calling', fn, 'with', id);
    return api[fn](id);
  };

  function initForceVariantSelection(info) {
    if (info._forceSelectInitialized) return;
    info._forceSelectInitialized = true;

    // Context. Embedded blocks (Featured Product) carry data-update-url="false";
    // quick view drawers carry data-view-mode="quick-view". Only the MAIN product
    // page should honor the URL's variant parameter.
    var syncsWithUrl = info.dataset.updateUrl !== 'false';
    var isQuickView = info.dataset.viewMode === 'quick-view';
    var isMainProduct = syncsWithUrl && !isQuickView;

    // Forced selection is skipped when the customer arrived on a direct
    // ?variant= link — they have already chosen. It is ALSO skipped when there
    // is nothing to choose (single variant, or no option groups rendered).
    // In every one of those cases the STOQ sync below still runs; that is the
    // whole point of splitting this flag out from an early return.
    var forceSelection = !(originalUrlHasVariant && isMainProduct);

    var variantScript = info.querySelector('script[type="application/json"]');
    var variants = [];
    if (variantScript) {
      try {
        variants = JSON.parse(variantScript.textContent);
      } catch (e) {
        variants = [];
      }
    }

    var initialVS = info.querySelector('variant-selects');
    var optionGroups = (initialVS || info).querySelectorAll('.product-form__input');
    var totalOptionGroups = optionGroups.length;

    if (!variants || variants.length <= 1 || totalOptionGroups < 1) {
      forceSelection = false;
    }

    log('init', {
      forceSelection: forceSelection,
      optionGroups: totalOptionGroups,
      variants: variants.length,
      urlHadVariant: originalUrlHasVariant,
    });

    var selectedGroups = new Set();
    var observer = null;
    var buttonObserver = null;
    var suppressObserver = false;

    // The last variant id we asked STOQ to paint. Guards against a loop —
    // STOQ's own DOM writes re-trigger our observers, and without this we
    // would ask it to repaint forever.
    var lastStoqVariantId = null;

    // Budgeted ONLY for the button tug-of-war with STOQ while the selection is
    // incomplete. Reset on every customer interaction. See BUDGET RULE above.
    var buttonReassertsLeft = BUTTON_REASSERT_BUDGET;

    // ---- Helpers --------------------------------------------------------

    // When we are not forcing selection there is nothing outstanding, so the
    // answer is always yes. reconcile() then always hands the button to STOQ,
    // and the button observer bows out instead of fighting it.
    function allOptionsChosen() {
      if (!forceSelection) return true;
      return selectedGroups.size >= totalOptionGroups;
    }

    function getVariantIdInput() {
      return (
        info.querySelector('input[name="id"].product-variant-id') ||
        info.querySelector('form[is="product-form"] input[name="id"]') ||
        info.querySelector('input[name="id"]')
      );
    }

    // Returns a NUMBER, not a string. STOQ's internal state keys variants by
    // s.selected_variant_id, which Shopify supplies numerically; its lookups
    // are strict, so passing input.value ("47425595080949") silently resolves
    // to no selling plan and renderButtonForVariant does nothing. The docs say
    // "variantId (String)" but the shipped embed disagrees.
    function currentVariantId() {
      var input = getVariantIdInput();
      var raw = input && input.value ? String(input.value).trim() : '';
      if (!raw) return null;
      var num = Number(raw);
      return Number.isFinite(num) && num > 0 ? num : null;
    }

    // Hand the button back to STOQ. Called once every option group has been
    // chosen (or immediately, when we are not forcing selection) and the theme
    // has finished writing its own markup, so STOQ gets the last word on the
    // label, price and payment widget.
    function syncStoq() {
      if (!stoqReady()) {
        log('syncStoq skipped: _RestockRocket.renderButtonForVariant unavailable');
        return;
      }
      var id = currentVariantId();
      if (!id) {
        log('syncStoq skipped: no variant id in the form');
        return;
      }
      if (id === lastStoqVariantId) return;
      lastStoqVariantId = id;
      var api = stoqApi();
      if (DEBUG && typeof api.getSellingPlan === 'function') {
        // If this logs undefined, STOQ has no preorder offer covering this
        // variant and no amount of repainting will produce a Preorder button.
        try {
          log('selling plan for', id, '->', api.getSellingPlan(id));
        } catch (e) {
          log('getSellingPlan threw for', id, e);
        }
      }
      log('syncStoq -> renderButtonForVariant', id);
      try {
        api.renderButtonForVariant(id);
      } catch (e) {
        // Never let an app error break the product form.
        console.warn('[R88] STOQ renderButtonForVariant failed', e);
      }
    }

    // ---- Price masking ---------------------------------------------------
    // The price node persists across renders (product-info.js only rewrites
    // its innerHTML), so we can observe it once and keep the reference.
    var priceNode = null;
    var priceMasked = false;
    var maskedHtml = null;
    var maskTimer = null;

    function getPriceNode() {
      return (
        (info.dataset.section && info.querySelector('#price-' + info.dataset.section)) ||
        info.querySelector('[id^="price-"]')
      );
    }

    function revealPrice() {
      if (maskTimer) {
        clearTimeout(maskTimer);
        maskTimer = null;
      }
      if (!priceMasked) return;
      priceMasked = false;
      maskedHtml = null;
      if (priceNode) priceNode.style.opacity = '';
      log('price revealed');
    }

    // Called synchronously from the theme's variant:changed, i.e. after the
    // theme has written the raw server price but before the browser paints.
    // We snapshot exactly what the theme wrote so the observer below can tell
    // the theme's write apart from STOQ's later decoration.
    function maskPrice() {
      if (!MASK_PRICE_DURING_SYNC || !stoqReady()) return;
      priceNode = priceNode || getPriceNode();
      if (!priceNode) return;
      maskedHtml = priceNode.innerHTML;
      if (!priceMasked) {
        priceMasked = true;
        priceNode.style.transition = 'opacity ' + MASK_FADE_MS + 'ms ease';
        priceNode.style.opacity = '0';
      }
      if (maskTimer) clearTimeout(maskTimer);
      maskTimer = setTimeout(revealPrice, MASK_MAX_MS);
      log('price masked, waiting for STOQ');
    }

    priceNode = getPriceNode();
    if (priceNode) {
      // childList/characterData only — NOT attributes, or our own opacity
      // write would trigger this and reveal the price immediately.
      new MutationObserver(function () {
        if (!priceMasked) return;
        // The theme's own write is already in maskedHtml. Anything different
        // is STOQ having decorated the node, which is our cue to fade back in.
        if (priceNode.innerHTML !== maskedHtml) revealPrice();
      }).observe(priceNode, { childList: true, subtree: true, characterData: true });
    }

    function disableAddToCart() {
      info.classList.add(INCOMPLETE_CLASS);

      var btn = info.querySelector('button[name="add"]');
      if (btn) {
        // Only write when state actually differs — redundant writes force
        // needless style recalculations and retrigger our observers.
        if (!btn.hasAttribute('disabled')) {
          btn.setAttribute('disabled', 'disabled');
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.6';
        }
        var btnText = btn.querySelector(':scope > span') || btn.querySelector('span');
        if (btnText && btnText.textContent !== 'Select all options') {
          btnText.textContent = 'Select all options';
        }
      }
      var stickyButtons = document.querySelectorAll('.sticky-atc-bar [name="add"]');
      for (var i = 0; i < stickyButtons.length; i++) {
        var b = stickyButtons[i];
        if (!b.hasAttribute('disabled')) {
          b.setAttribute('disabled', 'disabled');
          b.style.pointerEvents = 'none';
          b.style.opacity = '0.6';
        }
      }
    }

    // Clear a single option group (used for groups the user hasn't touched).
    function clearGroup(group) {
      // --- Dropdowns: inject/keep a selected placeholder option ---
      var selects = group.querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        var select = selects[s];
        var placeholder = select.querySelector('option[' + PLACEHOLDER_ATTR + ']');
        if (!placeholder) {
          placeholder = document.createElement('option');
          placeholder.setAttribute(PLACEHOLDER_ATTR, '');
          placeholder.setAttribute('disabled', 'disabled');
          placeholder.value = '';
          placeholder.textContent = PLACEHOLDER_TEXT;
          select.insertBefore(placeholder, select.firstChild);
        }
        // If the placeholder is already the active selection, this group is
        // already cleared — skip the per-option work entirely.
        if (select.selectedIndex === 0 && placeholder.hasAttribute('selected')) {
          continue;
        }
        // Remove `selected` from every real option, keep it on the placeholder
        // (the theme requires exactly one option to carry `selected`).
        var opts = select.options;
        for (var o = 0; o < opts.length; o++) {
          if (opts[o] !== placeholder) opts[o].removeAttribute('selected');
        }
        placeholder.setAttribute('selected', 'selected');
        select.selectedIndex = 0;
      }

      // --- Radios / swatches: uncheck everything ---
      var radios = group.querySelectorAll('input[type="radio"]:checked');
      for (var r = 0; r < radios.length; r++) {
        radios[r].checked = false;
        radios[r].removeAttribute('checked');
      }

      // --- Selected-value text label (e.g. "Pin Size: 15mm") ---
      var label = group.querySelector('[data-selected-swatch-value]');
      if (label && label.textContent !== '') label.textContent = '';
    }

    // The theme replaces the entire <variant-selects> node on each change
    // (see HTMLUpdateUtility.viewTransition in theme.js: it inserts a NEW node,
    // hides the old one, and removes it ~500ms later). During that overlap BOTH
    // nodes are in the DOM, so we must always resolve and query the CURRENT
    // (newest, visible) one rather than querying broadly from product-info,
    // which would return duplicate option groups from the stale node.
    function currentVariantSelects() {
      return info.querySelector('variant-selects'); // newest is first in document order
    }

    function getGroups() {
      var vs = currentVariantSelects();
      return (vs || info).querySelectorAll('.product-form__input');
    }

    function clearUnselectedGroups() {
      var groups = getGroups();
      for (var i = 0; i < groups.length; i++) {
        if (!selectedGroups.has(i)) clearGroup(groups[i]);
      }
    }

    // Run our DOM mutations without re-triggering the observers.
    function withObserverSuppressed(fn) {
      suppressObserver = true;
      try {
        fn();
      } finally {
        // Release on the next microtask so the mutations we just made are
        // flushed while still suppressed.
        Promise.resolve().then(function () {
          suppressObserver = false;
        });
      }
    }

    // Single place that decides who owns the buy button right now.
    // NOTE: the incomplete branch is deliberately unbudgeted.
    //
    // While the selection is PARTIAL we still let STOQ repaint, because the
    // theme has just overwritten #price-<section> with server HTML and the
    // customer would otherwise see the undiscounted price for a variant that
    // will actually check out at the preorder price. We repaint and then take
    // the button straight back, in the same frame, so there is no flicker.
    function reconcile(reason) {
      if (allOptionsChosen()) {
        info.classList.remove(INCOMPLETE_CLASS);
        log('reconcile: all options chosen', reason || '');
        syncStoq();
        return;
      }
      log('reconcile: incomplete, clearing', {
        reason: reason || '',
        chosen: selectedGroups.size,
        of: totalOptionGroups,
      });
      withObserverSuppressed(function () {
        clearUnselectedGroups();
        if (SYNC_STOQ_WHILE_INCOMPLETE) syncStoq();
        // Always last: STOQ may have just re-enabled and relabelled the button.
        disableAddToCart();
      });
    }

    // ---- Initial state --------------------------------------------------
    // Only blank things out when we are actually forcing a selection. On a
    // direct ?variant= link the customer's choice is already made, and wiping
    // it would both annoy them and destroy the variant STOQ needs.
    if (forceSelection) {
      info.currentVariant = null;
      info.lastKnownGoodVariant = null;

      withObserverSuppressed(function () {
        clearUnselectedGroups();
        disableAddToCart();
      });
    }

    // ---- Watch for the theme re-rendering the option HTML ---------------
    // CRITICAL: the theme does NOT mutate <variant-selects> in place — it
    // REPLACES the whole node (insert new sibling, hide+remove the old). So we
    // must observe a STABLE ancestor that survives the swap. We watch the
    // parent of variant-selects; when the new node is inserted (a childList
    // change on the parent), this fires AFTER the swap and after the theme's
    // own updateButtonsState, so re-disabling wins — and it is also our cue to
    // hand the button to STOQ once everything is set.
    //
    // PERFORMANCE: childList/subtree only (NOT attributes, which would fire on
    // every class/style toggle during the transition). All mutations from a
    // single render are coalesced into ONE pass via requestAnimationFrame.
    var variantSelects = currentVariantSelects();
    var observeTarget = variantSelects && variantSelects.parentNode ? variantSelects.parentNode : info;
    if (observeTarget) {
      var cleanupScheduled = false;
      observer = new MutationObserver(function () {
        if (suppressObserver || cleanupScheduled) return;
        cleanupScheduled = true;
        requestAnimationFrame(function () {
          cleanupScheduled = false;
          reconcile('variant-selects swap');
        });
      });
      observer.observe(observeTarget, { childList: true, subtree: true });
    }

    // ---- Watch the buy button itself ------------------------------------
    // STOQ's app embed loads asynchronously and repaints this button. Without
    // this observer, STOQ's initial paint leaves an enabled "Preorder" button
    // sitting above empty dropdowns. disableAddToCart() is write-guarded, so
    // this cannot loop against itself; the budget only bounds a disagreement
    // with STOQ, and resets whenever the customer interacts.
    //
    // When forceSelection is false, allOptionsChosen() is true and this
    // observer returns immediately every time — STOQ owns the button outright.
    var buttonHost =
      info.querySelector('.product-form__buttons') ||
      info.querySelector('form[is="product-form"]') ||
      info;
    if (buttonHost) {
      var buttonPassScheduled = false;
      buttonObserver = new MutationObserver(function () {
        if (suppressObserver || buttonPassScheduled) return;
        if (allOptionsChosen()) return; // STOQ owns it now — don't interfere
        buttonPassScheduled = true;
        requestAnimationFrame(function () {
          buttonPassScheduled = false;
          if (allOptionsChosen()) return;
          if (buttonReassertsLeft <= 0) {
            log('button re-assert budget spent; leaving the button alone');
            return;
          }
          buttonReassertsLeft--;
          withObserverSuppressed(function () {
            disableAddToCart();
          });
        });
      });
      buttonObserver.observe(buttonHost, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    // ---- Track which groups the customer actually interacts with --------
    info.addEventListener('change', function (event) {
      var group = event.target.closest('.product-form__input');
      if (!group) return;
      // Scope the index lookup to the variant-selects that owns the event,
      // so a stale node lingering during the transition can't shift indices.
      var scope = event.target.closest('variant-selects') || currentVariantSelects() || info;
      var groups = scope.querySelectorAll('.product-form__input');
      var index = Array.prototype.indexOf.call(groups, group);
      if (index !== -1) selectedGroups.add(index);
      // A real interaction: give the button guard a fresh budget.
      buttonReassertsLeft = BUTTON_REASSERT_BUDGET;
      log('option chosen', { index: index, chosen: selectedGroups.size, of: totalOptionGroups });
    });

    // ---- The theme's own re-render signal --------------------------------
    // product-info.js dispatches `variant:changed` on document as the LAST
    // step of handleUpdateProductInfo — after it has overwritten
    // #price-<section> and rewritten the button label. That makes it the exact
    // moment STOQ's markup is known to be gone, so we clear the repaint guard
    // and let syncStoq() run again even if the resolved variant id happens to
    // be unchanged (e.g. picking Make: Ford when the default variant was
    // already a Ford — same id, but the price node was still wiped).
    //
    // This is deliberately a theme event rather than a MutationObserver:
    // STOQ's own DOM writes cannot trigger it, so resetting the guard here
    // can't feed back into an endless repaint loop.
    document.addEventListener('variant:changed', function () {
      if (!info.isConnected) return;
      lastStoqVariantId = null;

      // Hide the price the instant the theme writes it, before the browser
      // paints. STOQ's repaint below is async internally, so this is what
      // actually stops the $1,000 flashing — the synchronous call alone is
      // not enough. The observer above fades it back in the moment STOQ
      // decorates the node.
      maskPrice();

      withObserverSuppressed(function () {
        syncStoq();
      });

      // The rest — clearing untouched groups, re-locking the button — can wait
      // for the frame. It also runs before paint, so there is no flicker.
      requestAnimationFrame(function () {
        reconcile('theme variant:changed');
      });
    });

    // ---- STOQ lifecycle --------------------------------------------------
    // STOQ fires this on window once its embed is ready. If the customer has
    // already finished choosing by then, hand the button straight to STOQ;
    // otherwise re-assert the locked state STOQ just painted over.
    window.addEventListener('stoq:loaded', function (event) {
      var detail = (event && event.detail) || {};
      log('stoq:loaded', detail.pageType, 'enabled:', detail.enabled);
      if (detail.enabled === false) return;
      requestAnimationFrame(function () {
        reconcile('stoq:loaded');
      });
    });

    // Belt and braces: STOQ paints a moment after `stoq:loaded` on some pages,
    // and the embed can also finish loading before this script runs (in which
    // case the event above never fires for us). Stops once STOQ is up.
    var settleTicks = 0;
    var settleTimer = setInterval(function () {
      settleTicks++;
      if (stoqReady()) {
        reconcile('settle tick ' + settleTicks);
        clearInterval(settleTimer);
        return;
      }
      if (settleTicks >= 10) {
        log('STOQ never appeared after ' + settleTicks + ' ticks');
        clearInterval(settleTimer);
      }
    }, 300);
  }

  // Patch the custom element so EVERY instance runs our logic on connect.
  function patchProductInfo() {
    var ctor = customElements.get('product-info');
    if (!ctor) return false;

    var proto = ctor.prototype;
    if (proto._forceSelectPatched) return true;
    proto._forceSelectPatched = true;

    var originalConnected = proto.connectedCallback;
    proto.connectedCallback = function () {
      if (typeof originalConnected === 'function') {
        originalConnected.apply(this, arguments);
      }
      var self = this;
      requestAnimationFrame(function () {
        initForceVariantSelection(self);
      });
    };

    // Handle any product-info elements already connected before this ran.
    document.querySelectorAll('product-info').forEach(initForceVariantSelection);
    return true;
  }

  if (window.customElements) {
    if (!patchProductInfo()) {
      customElements.whenDefined('product-info').then(patchProductInfo);
    }
  }
})();